import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../SEO";
import { Reveal } from "../Reveal";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { formatCurrencyCompact } from "../../data/currencies";

// Each option keeps a canonical English `value` (used by scoring, storage and
// the answer-matching tables below) plus per-language display text - so the
// UI can be shown in Hindi without touching any scoring logic.
const opt = (value, en, hi) => ({ value, en, hi: hi || en });

const ROLE_OPTIONS = [
  opt("Nothing", "Nothing", "कुछ नहीं"),
  opt("Freelancer", "Freelancer", "फ्रीलांसर"),
  opt("Job", "Job", "नौकरी"),
  opt("Business", "Business", "व्यवसाय"),
  opt("Digital Marketing Agency", "Digital Marketing Agency", "डिजिटल मार्केटिंग एजेंसी"),
];
const LOCATION_OPTIONS = [
  opt("Tier 1", "Tier 1", "टियर 1"),
  opt("Tier 2", "Tier 2", "टियर 2"),
  opt("Tier 3", "Tier 3", "टियर 3"),
  opt("City name", "City name", "शहर का नाम"),
];
const CHANNEL_OPTIONS = [
  opt("Instagram", "Instagram"),
  opt("Google", "Google"),
  opt("Facebook", "Facebook"),
  opt("Website", "Website", "वेबसाइट"),
  opt("AI", "AI"),
];
const TERM_OPTIONS = [
  opt("SEO", "SEO"),
  opt("Google Ranking", "Google Ranking", "गूगल रैंकिंग"),
  opt("Social Media", "Social Media", "सोशल मीडिया"),
  opt("Leads", "Leads", "लीड्स"),
];
const DONE_BUSINESS_OPTIONS = [opt("Yes", "Yes", "हाँ"), opt("No", "No", "नहीं")];
const COMFORT_OPTIONS = [
  opt("Calls", "Calls", "कॉल्स"),
  opt("Meeting", "Meeting", "मीटिंग"),
  opt("Online", "Online", "ऑनलाइन"),
];
const READY_OPTIONS = [opt("Yes", "Yes", "हाँ"), opt("No", "No", "नहीं"), opt("Maybe", "Maybe", "शायद")];
const DAILY_CALLS_OPTIONS = [
  opt("5/day", "5/day", "5/दिन"),
  opt("10/day", "10/day", "10/दिन"),
  opt("20/day", "20/day", "20/दिन"),
];
const YEARLY_DEALS_OPTIONS = [
  opt("20/year", "20/year", "20/वर्ष"),
  opt("50/year", "50/year", "50/वर्ष"),
  opt("100/year", "100/year", "100/वर्ष"),
];
const INVESTMENT_OPTIONS = [opt("₹10K", "₹10K"), opt("₹20K", "₹20K"), opt("₹30K", "₹30K")];
const TOTAL_QUESTIONS = 10;

// Per-question point tables used by computeScore(). Each table's own max
// contributes to MAX_RAW_SCORE below, so the final score always normalizes to 0-100
// regardless of how the individual weights are tuned. Keyed by canonical (English) value.
const ROLE_POINTS = { "Digital Marketing Agency": 20, Business: 16, Freelancer: 11, Job: 6, Nothing: 2 };
const LOCATION_POINTS = { "Tier 1": 5, "Tier 2": 4, "Tier 3": 3 };
const LOCATION_POINTS_DEFAULT = 3; // typed city name
const CHANNELS_MAX = 10;
const TERMS_MAX = 10;
const DONE_BUSINESS_POINTS = { Yes: 10, No: 3 };
const COMFORT_MAX = 5;
const READY_POINTS = { Yes: 10, Maybe: 5, No: 0 };
const DAILY_CALLS_POINTS = { "20/day": 10, "10/day": 6, "5/day": 3 };
const YEARLY_DEALS_POINTS = { "100/year": 10, "50/year": 6, "20/year": 3 };
const INVESTMENT_POINTS = { "₹30K": 15, "₹20K": 10, "₹10K": 5 };

const MAX_RAW_SCORE =
  20 /* role */ + 5 /* location */ + CHANNELS_MAX + TERMS_MAX + 10 /* done business */ +
  5 /* comfort */ + 10 /* ready */ + 10 /* daily calls */ + 10 /* yearly deals */ + 15 /* investment */;

const SCORE_TIERS = {
  strong: {
    label: { en: "Strong Fit", hi: "मजबूत फिट" },
    message: {
      en: "You already have the base to start selling MagicBolt today.",
      hi: "आपके पास आज ही मैजिकबोल्ट बेचना शुरू करने का आधार मौजूद है।",
    },
  },
  good: {
    label: { en: "Good Fit", hi: "अच्छा फिट" },
    message: {
      en: "You have solid potential - a little ramp-up and you're set.",
      hi: "आपमें अच्छी संभावना है - थोड़ी तैयारी के साथ आप तैयार हैं।",
    },
  },
  early: {
    label: { en: "Early Stage", hi: "शुरुआती चरण" },
    message: {
      en: "You can still start, but you'll want to build a few basics first.",
      hi: "आप अभी भी शुरू कर सकते हैं, पर पहले कुछ बुनियादी बातें सीखनी होंगी।",
    },
  },
};

function computeScore(answers) {
  const raw =
    (ROLE_POINTS[answers.role] || 0) +
    (LOCATION_POINTS[answers.location] ?? LOCATION_POINTS_DEFAULT) +
    Math.round((Math.min(answers.channels.length, CHANNEL_OPTIONS.length) / CHANNEL_OPTIONS.length) * CHANNELS_MAX) +
    Math.round((Math.min(answers.familiarTerms.length, TERM_OPTIONS.length) / TERM_OPTIONS.length) * TERMS_MAX) +
    (DONE_BUSINESS_POINTS[answers.doneBusiness] || 0) +
    Math.round((Math.min(answers.comfortIn.length, COMFORT_OPTIONS.length) / COMFORT_OPTIONS.length) * COMFORT_MAX) +
    (READY_POINTS[answers.readyToLearn] || 0) +
    (DAILY_CALLS_POINTS[answers.dailyCalls] || 0) +
    (YEARLY_DEALS_POINTS[answers.yearlyDeals] || 0) +
    (INVESTMENT_POINTS[answers.investment] || 0);

  const score = Math.round((raw / MAX_RAW_SCORE) * 100);
  const tierKey = score >= 75 ? "strong" : score >= 45 ? "good" : "early";

  return { score, tier: SCORE_TIERS[tierKey] };
}

// Mirrors the "Growth" package price used by the Agency Earning Calculator on
// /how-agencies-earn (ALL_CHANNELS_COMBINED.prices[1]) so both pages quote the
// same numbers. clients/year comes straight from the "yearly deals" answer.
const GROWTH_TIER_MONTHLY_INR = 20000;
const INR_CURRENCY = { code: "INR", symbol: "₹" };
// Same illustrative ±15% band the /how-agencies-earn calculators use for their ranges.
const RANGE_BAND = 0.15;

const formatINR = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
const formatRange = (low, high) =>
  `${formatCurrencyCompact(low, INR_CURRENCY)} – ${formatCurrencyCompact(high, INR_CURRENCY)}`;

function estimateEarnings(answers) {
  const clientsPerYear = parseInt(answers.yearlyDeals, 10) || 0;
  const monthly = clientsPerYear * GROWTH_TIER_MONTHLY_INR;
  const yearly = monthly * 12;
  return {
    clientsPerYear,
    monthly,
    yearly,
    monthlyLow: monthly * (1 - RANGE_BAND),
    monthlyHigh: monthly * (1 + RANGE_BAND),
    yearlyLow: yearly * (1 - RANGE_BAND),
    yearlyHigh: yearly * (1 + RANGE_BAND),
  };
}

// All static (non-question) UI copy, in both languages.
const STRINGS = {
  heading: { en: "Know How Much You Can Earn?", hi: "जानें आप कितना कमा सकते हैं?" },
  subtitle: {
    en: "This helps us score your fit and point you to the right plan.",
    hi: "इससे हमें आपकी उपयुक्तता आंकने और सही प्लान सुझाने में मदद मिलती है।",
  },
  selectAllHint: { en: "(select all that apply)", hi: "(जो लागू हों सभी चुनें)" },
  selectKnownHint: { en: "(select all you know)", hi: "(जो जानते हों सभी चुनें)" },
  cityPlaceholder: { en: "Type your city", hi: "अपने शहर का नाम लिखें" },
  allChip: { en: "All", hi: "सभी" },
  answeredOf: { en: "answered", hi: "उत्तर दिए" },
  seeMyScore: { en: "See My Score", hi: "मेरा स्कोर देखें" },
  fitScoreTitle: { en: "My Earning Score", hi: "मेरा अर्निंग स्कोर" },
  outOf100: { en: "out of 100", hi: "100 में से" },
  estimateEarningsTitle: { en: "Estimate Your Earnings", hi: "अपनी कमाई का अनुमान लगाएं" },
  approxMonthly: { en: "Approx. Monthly", hi: "लगभग मासिक" },
  approxAnnual: { en: "Approx. Annual", hi: "लगभग वार्षिक" },
  earningsDisclaimer: {
    en: "Illustrative gross revenue, not profit - not a guarantee of earnings.",
    hi: "यह अनुमानित सकल राजस्व है, लाभ नहीं - कमाई की कोई गारंटी नहीं।",
  },
  earningsNote: {
    en: "Note: This is just a prediction - if you work like a proper business, it can be achievable.",
    hi: "नोट: यह सिर्फ एक अनुमान है - अगर आप इसे एक सही व्यवसाय की तरह करें, तो यह हासिल किया जा सकता है।",
  },
  seeHowAgenciesEarn: { en: "See How Agencies Earn", hi: "एजेंसियां कैसे कमाती हैं देखें" },
  questions: {
    role: { en: "What do you currently do?", hi: "आप अभी क्या करते हैं?" },
    location: { en: "Where do you live?", hi: "आप कहाँ रहते हैं?" },
    channels: { en: "Which platforms do you already use?", hi: "आप पहले से किन प्लेटफ़ॉर्म का उपयोग करते हैं?" },
    terms: {
      en: "Which marketing terms are you familiar with?",
      hi: "आप किन मार्केटिंग शब्दों से परिचित हैं?",
    },
    doneBusiness: {
      en: "Have you run or owned a business before?",
      hi: "क्या आपने पहले कभी व्यवसाय चलाया है या मालिक रहे हैं?",
    },
    comfort: { en: "How do you prefer to connect with clients?", hi: "आप ग्राहकों से जुड़ने के लिए क्या पसंद करते हैं?" },
    ready: {
      en: "Are you willing to learn new skills for this?",
      hi: "क्या आप इसके लिए नए कौशल सीखने को तैयार हैं?",
    },
    dailyCalls: {
      en: "If you have a contact list of businesses, how many calls or meetings can you make daily?",
      hi: "अगर आपके पास व्यवसायों की संपर्क सूची हो, तो आप रोज़ाना कितनी कॉल या मीटिंग कर सकते हैं?",
    },
    yearlyDeals: {
      en: "Realistically, how many customers can you convert in a year if we help you?",
      hi: "वास्तविक रूप से, अगर हम आपकी मदद करें तो आप एक साल में कितने ग्राहक कन्वर्ट कर सकते हैं?",
    },
    investment: {
      en: "How much can you invest to earn ₹1–2 lakh/month?",
      hi: "₹1-2 लाख/माह कमाने के लिए आप कितना निवेश कर सकते हैं?",
    },
  },
};

const Chip = ({ label, selected, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={selected}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
      selected
        ? "border-[#FF6B00] bg-[#FF6B00] text-white shadow-[0_4px_14px_rgba(255,107,0,0.25)]"
        : "border-[#E6E9F1] bg-white text-[#475569] hover:border-[#FF6B00]/50 hover:text-[#0B1533]"
    }`}
  >
    {label}
  </button>
);

const SingleChoiceGroup = ({ options, value, onChange, testId, lang }) => (
  <div className="flex flex-wrap gap-2" data-testid={testId}>
    {options.map((o) => (
      <Chip
        key={o.value}
        label={o[lang]}
        selected={value === o.value}
        onClick={() => onChange(o.value)}
        testId={`${testId}-${o.value}`}
      />
    ))}
  </div>
);

const MultiChoiceGroup = ({ options, values, onToggle, onSelectAll, testId, lang }) => {
  const allSelected = values.length === options.length;
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      <Chip
        label={STRINGS.allChip[lang]}
        selected={allSelected}
        onClick={() => onSelectAll(allSelected ? [] : options.map((o) => o.value))}
        testId={`${testId}-all`}
      />
      {options.map((o) => (
        <Chip
          key={o.value}
          label={o[lang]}
          selected={values.includes(o.value)}
          onClick={() => onToggle(o.value)}
          testId={`${testId}-${o.value}`}
        />
      ))}
    </div>
  );
};

const toggleInArray = (arr, value) =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

const Question = ({ delay, label, hint, children }) => (
  <Reveal delay={delay} className="py-4 border-b border-[#E6E9F1] last:border-b-0">
    <h2 className="text-sm font-semibold text-[#0B1533] mb-2.5">
      {label} {hint && <span className="font-normal text-[#94A3B8]">{hint}</span>}
    </h2>
    {children}
  </Reveal>
);

const LangToggle = ({ lang, setLang }) => (
  <div className="inline-flex rounded-full border border-[#E6E9F1] bg-white p-0.5" data-testid="lang-toggle">
    {["en", "hi"].map((code) => (
      <button
        key={code}
        type="button"
        onClick={() => setLang(code)}
        data-testid={`lang-toggle-${code}`}
        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
          lang === code ? "bg-[#FF6B00] text-white" : "text-[#475569] hover:text-[#0B1533]"
        }`}
      >
        {code === "en" ? "English" : "हिंदी"}
      </button>
    ))}
  </div>
);

export default function ExamineCustomerPage() {
  const navigate = useNavigate();
  const [lang, setLang] = useState("en");
  const [role, setRole] = useState(null);
  const [location, setLocation] = useState(null);
  const [city, setCity] = useState("");
  const [channels, setChannels] = useState([]);
  const [terms, setTerms] = useState([]);
  const [doneBusiness, setDoneBusiness] = useState(null);
  const [comfortIn, setComfortIn] = useState([]);
  const [readyToLearn, setReadyToLearn] = useState(null);
  const [dailyCalls, setDailyCalls] = useState(null);
  const [yearlyDeals, setYearlyDeals] = useState(null);
  const [investment, setInvestment] = useState(null);
  const [result, setResult] = useState(null);

  // Someone already running a business/agency has implicitly answered "have you
  // run a business" - skip asking and count it as answered ("Yes").
  const isBusinessRole = role === "Business" || role === "Digital Marketing Agency";
  const effectiveDoneBusiness = isBusinessRole ? "Yes" : doneBusiness;

  const locationAnswered = Boolean(location && (location !== "City name" || city.trim().length > 0));
  const answeredCount = [
    Boolean(role),
    locationAnswered,
    channels.length > 0,
    terms.length > 0,
    Boolean(effectiveDoneBusiness),
    comfortIn.length > 0,
    Boolean(readyToLearn),
    Boolean(dailyCalls),
    Boolean(yearlyDeals),
    Boolean(investment),
  ].filter(Boolean).length;
  const canContinue = answeredCount === TOTAL_QUESTIONS;

  const handleContinue = () => {
    if (!canContinue) return;
    const profile = {
      role,
      location: location === "City name" ? city.trim() : location,
      channels,
      familiarTerms: terms,
      doneBusiness: effectiveDoneBusiness,
      comfortIn,
      readyToLearn,
      dailyCalls,
      yearlyDeals,
      investment,
    };
    const scoreResult = computeScore(profile);
    const earnings = estimateEarnings(profile);
    try {
      localStorage.setItem(
        "mb_customer_profile",
        JSON.stringify({ ...profile, score: scoreResult.score, tier: scoreResult.tier.label.en, earnings })
      );
    } catch {
      // localStorage unavailable (private mode, etc.) - proceed without persisting
    }
    setResult({ ...scoreResult, earnings });
    window.scrollTo(0, 0);
  };

  if (result) {
    return (
      <div className="min-h-screen bg-[#F4F6FB]" data-testid="examine-customer-result">
        <SEO
          title="Your MagicBolt Fit Score | MagicBolt AI"
          description="Your eligibility score to sell MagicBolt AI products and services."
          path="/start"
          noindex
        />
        <header className="pt-6 pb-4 sm:pt-8 sm:pb-5 flex items-center justify-center gap-4">
          <Link to="/" data-testid="examine-customer-logo">
            <img src="/logos/magicbolt.webp" alt="MagicBolt AI" className="h-7 sm:h-8 w-auto object-contain" />
          </Link>
          <LangToggle lang={lang} setLang={setLang} />
        </header>
        <main className="max-w-md md:max-w-xl lg:max-w-2xl mx-auto px-4 sm:px-6 pb-16">
          <Reveal className="rounded-xl border border-[#E6E9F1] bg-white p-8 text-center">
            <span className="text-xs uppercase tracking-[0.2em] text-[#FF6B00] font-semibold">
              {STRINGS.fitScoreTitle[lang]}
            </span>
            <div className="mt-3 text-6xl font-extrabold text-[#0B1533] tracking-tight" data-testid="score-value">
              {result.score}
            </div>
            <div className="mt-1 text-sm text-[#94A3B8]">{STRINGS.outOf100[lang]}</div>
            <div
              className="mt-5 inline-block rounded-full bg-[#FFF3E9] text-[#FF6B00] font-semibold text-sm px-4 py-1.5"
              data-testid="score-label"
            >
              {result.tier.label[lang]}
            </div>
            <p className="mt-4 text-sm text-[#475569] leading-relaxed">{result.tier.message[lang]}</p>
          </Reveal>

          <Reveal delay={0.1} className="mt-4 rounded-xl border border-[#E6E9F1] bg-white p-8 text-center">
            <span className="text-xs uppercase tracking-[0.2em] text-[#FF6B00] font-semibold">
              {STRINGS.estimateEarningsTitle[lang]}
            </span>
            <p className="mt-2 text-xs text-[#94A3B8]">
              {lang === "hi"
                ? `लगभग ${result.earnings.clientsPerYear} ग्राहक/वर्ष के आधार पर, मैजिकबोल्ट के ग्रोथ पैकेज (${formatINR(GROWTH_TIER_MONTHLY_INR)}/माह प्रति ग्राहक) पर`
                : `Based on ~${result.earnings.clientsPerYear} clients/year at MagicBolt's Growth package (${formatINR(GROWTH_TIER_MONTHLY_INR)}/mo per client)`}
            </p>
            <div className="mt-5 grid gap-3">
              <div className="rounded-lg bg-[#FFF3E9] p-4">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[#FF6B00]">
                  {STRINGS.approxMonthly[lang]}
                </div>
                <div
                  className="mt-1 text-2xl font-extrabold text-[#0B1533] tracking-tight"
                  data-testid="earnings-monthly"
                >
                  {formatRange(result.earnings.monthlyLow, result.earnings.monthlyHigh)}
                </div>
              </div>
              <div className="rounded-lg bg-[#F4F6FB] p-4">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8]">
                  {STRINGS.approxAnnual[lang]}
                </div>
                <div
                  className="mt-1 text-2xl font-extrabold text-[#0B1533] tracking-tight"
                  data-testid="earnings-yearly"
                >
                  {formatRange(result.earnings.yearlyLow, result.earnings.yearlyHigh)}
                </div>
              </div>
            </div>
            <p className="mt-4 text-[11px] text-[#94A3B8] leading-relaxed">{STRINGS.earningsDisclaimer[lang]}</p>
            <p className="mt-2 text-[11px] font-medium text-[#FF6B00] leading-relaxed">{STRINGS.earningsNote[lang]}</p>

            <Button
              size="lg"
              onClick={() => navigate("/how-agencies-earn")}
              data-testid="see-how-agencies-earn-button"
              className="mt-6 w-full"
            >
              {STRINGS.seeHowAgenciesEarn[lang]} <ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
          </Reveal>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="examine-customer-page">
      <SEO
        title="Tell Us About You | MagicBolt AI"
        description="A few quick questions to point you to the right MagicBolt AI plan."
        path="/start"
        noindex
      />

      <header className="pt-6 pb-4 sm:pt-8 sm:pb-5 flex justify-center">
        <Link to="/" data-testid="examine-customer-logo">
          <img src="/logos/magicbolt.webp" alt="MagicBolt AI" className="h-7 sm:h-8 w-auto object-contain" />
        </Link>
      </header>

      <main className="max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto px-4 sm:px-6 pb-28">
        <Reveal className="text-center mb-5">
          <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-[#0B1533]">
            {STRINGS.heading[lang]}
          </h1>
          <p className="mt-1.5 text-sm text-[#64748B]">{STRINGS.subtitle[lang]}</p>
          <div className="mt-3 flex justify-center">
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </Reveal>

        <div className="rounded-xl border border-[#E6E9F1] bg-white px-4 sm:px-5">
          <Question delay={0.02} label={STRINGS.questions.location[lang]}>
            <SingleChoiceGroup
              options={LOCATION_OPTIONS}
              value={location}
              onChange={setLocation}
              testId="location-option"
              lang={lang}
            />
            {location === "City name" && (
              <Input
                autoFocus
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={STRINGS.cityPlaceholder[lang]}
                data-testid="city-input"
                className="mt-2.5 h-10 border-[#E6E9F1] focus-visible:ring-[#FF6B00]"
              />
            )}
          </Question>

          <Question delay={0.04} label={STRINGS.questions.role[lang]}>
            <SingleChoiceGroup options={ROLE_OPTIONS} value={role} onChange={setRole} testId="role-option" lang={lang} />
          </Question>

          <Question delay={0.06} label={STRINGS.questions.channels[lang]} hint={STRINGS.selectAllHint[lang]}>
            <MultiChoiceGroup
              options={CHANNEL_OPTIONS}
              values={channels}
              onToggle={(v) => setChannels((prev) => toggleInArray(prev, v))}
              onSelectAll={setChannels}
              testId="channel-option"
              lang={lang}
            />
          </Question>

          <Question delay={0.08} label={STRINGS.questions.terms[lang]} hint={STRINGS.selectKnownHint[lang]}>
            <MultiChoiceGroup
              options={TERM_OPTIONS}
              values={terms}
              onToggle={(v) => setTerms((prev) => toggleInArray(prev, v))}
              onSelectAll={setTerms}
              testId="term-option"
              lang={lang}
            />
          </Question>

          {!isBusinessRole && (
            <Question delay={0.1} label={STRINGS.questions.doneBusiness[lang]}>
              <SingleChoiceGroup
                options={DONE_BUSINESS_OPTIONS}
                value={doneBusiness}
                onChange={setDoneBusiness}
                testId="done-business-option"
                lang={lang}
              />
            </Question>
          )}

          <Question delay={0.12} label={STRINGS.questions.comfort[lang]} hint={STRINGS.selectAllHint[lang]}>
            <MultiChoiceGroup
              options={COMFORT_OPTIONS}
              values={comfortIn}
              onToggle={(v) => setComfortIn((prev) => toggleInArray(prev, v))}
              onSelectAll={setComfortIn}
              testId="comfort-option"
              lang={lang}
            />
          </Question>

          <Question delay={0.14} label={STRINGS.questions.ready[lang]}>
            <SingleChoiceGroup
              options={READY_OPTIONS}
              value={readyToLearn}
              onChange={setReadyToLearn}
              testId="ready-option"
              lang={lang}
            />
          </Question>

          <Question delay={0.16} label={STRINGS.questions.dailyCalls[lang]}>
            <SingleChoiceGroup
              options={DAILY_CALLS_OPTIONS}
              value={dailyCalls}
              onChange={setDailyCalls}
              testId="daily-calls-option"
              lang={lang}
            />
          </Question>

          <Question delay={0.18} label={STRINGS.questions.yearlyDeals[lang]}>
            <SingleChoiceGroup
              options={YEARLY_DEALS_OPTIONS}
              value={yearlyDeals}
              onChange={setYearlyDeals}
              testId="yearly-deals-option"
              lang={lang}
            />
          </Question>

          <Question delay={0.2} label={STRINGS.questions.investment[lang]}>
            <SingleChoiceGroup
              options={INVESTMENT_OPTIONS}
              value={investment}
              onChange={setInvestment}
              testId="investment-option"
              lang={lang}
            />
          </Question>
        </div>
      </main>

      <div
        className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-md border-t border-[#E6E9F1] px-4 sm:px-6 py-3"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto flex items-center justify-between gap-4">
          <span className="text-xs text-[#94A3B8]" data-testid="answered-count">
            {answeredCount} / {TOTAL_QUESTIONS} {STRINGS.answeredOf[lang]}
          </span>
          <Button disabled={!canContinue} onClick={handleContinue} data-testid="examine-customer-continue">
            {STRINGS.seeMyScore[lang]} <ArrowRight className="ml-1.5 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
